import { AgeYears } from '../value-objects/AgeYears';
import { MoneyAmount } from '../value-objects/MoneyAmount';
import { fixedPMT, growingPMT, monthlyRate } from '../finance';
import { FireMethod, EventKind } from '../enums';
import { FireScenario } from '../entities/FireScenario';
import { ScenarioEvent } from '../entities/ScenarioEvent';
import { getPensionDefaults } from '../data/pensionValidation';

const MAX_HORIZON_AGE = 120;
const DWZ_END_AGE = 90;
const CLASSICAL_HORIZON_AGE = 100;

/**
 * Sentinel returned by `calculateRequiredMonthlyInvestmentAtAge` when no
 * finite monthly contribution can reach the FIRE number at the candidate
 * retirement age. The solver treats this as "infeasible at this age" and
 * advances to the next candidate `t`.
 */
const PMT_INFEASIBLE = new MoneyAmount(Number.POSITIVE_INFINITY);

export class FireCalculator {
  calculateFireNumber(scenario: FireScenario): MoneyAmount {
    return this.calculateAdjustedFireNumber(
      scenario,
      scenario.retirementAge.value,
      FireMethod.classicalFire,
    );
  }

  calculateDieWithZero(scenario: FireScenario): MoneyAmount {
    const base = this.calculateDieWithZeroAtAge(
      scenario,
      scenario.retirementAge.value,
    );
    return this.subtractRetirementPV(
      scenario,
      scenario.retirementAge.value,
      base,
      DWZ_END_AGE,
    );
  }

  /**
   * Compute the base DWZ target (no events) for a given retirement age.
   * Used by PlanService for Mode B simulation setup.
   */
  calculateDieWithZeroAtAge(
    scenario: FireScenario,
    retirementAge: number,
  ): MoneyAmount {
    const annualExpensesToday = scenario.monthlyExpenses.value * 12;
    const rreal =
      (1 + scenario.annualReturn.value) / (1 + scenario.inflation.value) - 1;
    const greal = 1 + rreal;
    const t = retirementAge - scenario.currentAge.value;

    if (t < 0) return MoneyAmount.zero;

    const tdwz = DWZ_END_AGE - retirementAge;
    if (tdwz <= 0) return MoneyAmount.zero;

    const firstYearWithdrawal = annualExpensesToday;

    if (Math.abs(rreal) < 1e-12) {
      return new MoneyAmount(firstYearWithdrawal * tdwz);
    }

    const fdwz =
      (firstYearWithdrawal * (Math.pow(greal, tdwz) - 1)) /
      (Math.pow(greal, tdwz - 1) * (greal - 1));
    return new MoneyAmount(fdwz);
  }

  /**
   * Compute the event-adjusted FIRE number for a given method and
   * retirement age.
   */
  private calculateAdjustedFireNumber(
    scenario: FireScenario,
    retirementAge: number,
    method: FireMethod,
  ): MoneyAmount {
    const base = this._calculateBaseTarget(scenario, retirementAge, method);
    const horizonAge = method === FireMethod.classicalFire
      ? CLASSICAL_HORIZON_AGE
      : DWZ_END_AGE;
    return this.subtractRetirementPV(scenario, retirementAge, base, horizonAge);
  }

  /**
   * Compute the base target (no events) for a given method and retirement age.
   */
  private _calculateBaseTarget(
    scenario: FireScenario,
    retirementAge: number,
    method: FireMethod,
  ): MoneyAmount {
    if (method === FireMethod.classicalFire) {
      const annualExpenses = scenario.monthlyExpenses.value * 12;
      return new MoneyAmount(annualExpenses / scenario.swr.value);
    }
    return this.calculateDieWithZeroAtAge(scenario, retirementAge);
  }

  /**
   * Subtract retirement-phase income PV and add expense PV from the base
   * target, clamping to >= 0. Uses 0% real discount rate.
   */
  private subtractRetirementPV(
    scenario: FireScenario,
    retirementAge: number,
    baseTarget: MoneyAmount,
    horizonAge: number,
  ): MoneyAmount {
    let pvIncome = 0;
    let pvExpense = 0;

    for (const event of scenario.events) {
      if (!event.enabled) continue;
      const pv = this.computeRetirementPhaseEventPV(
        scenario,
        event,
        retirementAge,
        horizonAge,
      );
      if (pv === 0) continue;

      if (event.kind === EventKind.income) {
        pvIncome += pv;
      } else {
        pvExpense += pv;
      }
    }

    const adjusted = Math.max(0, baseTarget.value - pvIncome + pvExpense);
    return new MoneyAmount(adjusted);
  }

  /**
   * Compute the present value (at 0% real discount rate) of a single event's
   * retirement-phase cashflows, expressed in today's CZK.
   *
   * @see FORMULAS.md §11.3
   */
  private computeRetirementPhaseEventPV(
    scenario: FireScenario,
    event: ScenarioEvent,
    retirementAge: number,
    horizonAge: number,
  ): number {
    const activeStart = Math.max(event.startAge.value, retirementAge);
    const activeEnd = event.durationYears === 0
      ? horizonAge
      : Math.min(event.startAge.value + event.durationYears, horizonAge);

    if (activeStart >= activeEnd) return 0;

    const growthRate = this.getEventGrowthRate(scenario, event);
    const i = scenario.inflation.value;
    const currentAge = scenario.currentAge.value;
    const annualAmount = event.frequency === 'monthly'
      ? event.amount.value * 12
      : event.amount.value;

    let pv = 0;
    for (let y = activeStart; y < activeEnd; y++) {
      const yearsFromNow = y - currentAge;
      const realContribution = annualAmount *
        Math.pow(1 + growthRate, yearsFromNow) /
        Math.pow(1 + i, yearsFromNow);
      pv += realContribution;
    }

    return pv;
  }

  /**
   * Compute the future value at retirement of a single event's
   * accumulation-phase cashflows. Cashflows compound at nominal return `r`.
   *
   * @see FORMULAS.md §11.5
   */
  private computeAccumulationPhaseEventFV(
    scenario: FireScenario,
    event: ScenarioEvent,
    retirementAge: number,
  ): number {
    if (event.startAge.value >= retirementAge) return 0;

    const activeStart = Math.max(event.startAge.value, scenario.currentAge.value);
    const activeEnd = event.durationYears === 0
      ? retirementAge
      : Math.min(event.startAge.value + event.durationYears, retirementAge);

    if (activeStart >= activeEnd) return 0;

    const growthRate = this.getEventGrowthRate(scenario, event);
    const r = scenario.annualReturn.value;
    const currentAge = scenario.currentAge.value;
    const annualAmount = event.frequency === 'monthly'
      ? event.amount.value * 12
      : event.amount.value;

    let fv = 0;
    for (let y = activeStart; y < activeEnd; y++) {
      const yearsUntilRetirement = retirementAge - y;
      const yearsFromNow = y - currentAge;
      const nominalAmount = annualAmount *
        Math.pow(1 + growthRate, yearsFromNow);
      const contribution = nominalAmount *
        Math.pow(1 + r, yearsUntilRetirement);
      fv += contribution;
    }

    return fv;
  }

  private getEventGrowthRate(
    scenario: FireScenario,
    event: ScenarioEvent,
  ): number {
    if (event.pensionType != null && event.pensionType !== 'none') {
      const defaultGrowth = getPensionDefaults(event.pensionType).growthRate;
      return event.growthRate ?? defaultGrowth;
    } else if (event.inflationAdjusted) {
      return scenario.inflation.value;
    } else {
      return 0;
    }
  }

  private computeAccumulationFVs(
    scenario: FireScenario,
    retirementAge: number,
  ): { fvIncome: number; fvExpense: number } {
    let fvIncome = 0;
    let fvExpense = 0;

    for (const event of scenario.events) {
      if (!event.enabled) continue;
      if (event.startAge.value >= retirementAge) continue;

      const fv = this.computeAccumulationPhaseEventFV(
        scenario,
        event,
        retirementAge,
      );
      if (fv === 0) continue;

      if (event.kind === EventKind.income) {
        fvIncome += fv;
      } else {
        fvExpense += fv;
      }
    }

    return { fvIncome, fvExpense };
  }

  calculateRequiredMonthlyInvestment(
    scenario: FireScenario,
    method: FireMethod,
  ): MoneyAmount {
    return this.calculateRequiredMonthlyInvestmentAtAge(
      scenario,
      method,
      scenario.retirementAge.value,
    );
  }

  /**
   * Required monthly contribution to reach the FIRE number at a candidate
   * retirement age. Event-aware per FORMULAS.md §11.
   */
  calculateRequiredMonthlyInvestmentAtAge(
    scenario: FireScenario,
    method: FireMethod,
    retirementAge: number,
  ): MoneyAmount {
    const fireNumber = this.calculateAdjustedFireNumber(
      scenario,
      retirementAge,
      method,
    );

    const t = retirementAge - scenario.currentAge.value;
    const n = t * 12;

    if (n <= 0) {
      return scenario.currentPortfolio.value >= fireNumber.value
        ? MoneyAmount.zero
        : PMT_INFEASIBLE;
    }

    const targetFV =
      fireNumber.value * Math.pow(1 + scenario.inflation.value, t);
    const rm = monthlyRate(scenario.annualReturn.value);
    const fvExisting =
      scenario.currentPortfolio.value * Math.pow(1 + rm, n);

    const { fvIncome, fvExpense } = this.computeAccumulationFVs(
      scenario,
      retirementAge,
    );

    const adjustedGap = targetFV - fvExisting - fvIncome + fvExpense;
    if (adjustedGap <= 0) return MoneyAmount.zero;

    if (scenario.contributionGrowthEnabled) {
      return new MoneyAmount(
        growingPMT(adjustedGap, rm, scenario.inflation.value / 12, n),
      );
    }
    return new MoneyAmount(fixedPMT(adjustedGap, rm, n));
  }

  solveForRetirementAge(
    scenario: FireScenario,
    method: FireMethod,
  ): AgeYears {
    const target = scenario.monthlyContribution.value;
    const currentAge = scenario.currentAge.value;

    if (currentAge <= 0) {
      return new AgeYears(currentAge);
    }

    if (target <= 0) {
      return new AgeYears(MAX_HORIZON_AGE);
    }

    const maxYears = MAX_HORIZON_AGE - currentAge;

    let earliest = -1;
    for (let t = 0; t <= maxYears; t++) {
      const required = this.calculateRequiredMonthlyInvestmentAtAge(
        scenario,
        method,
        currentAge + t,
      );
      if (required.value <= target && earliest === -1) {
        earliest = t;
        break;
      }
    }

    if (earliest === -1) {
      return new AgeYears(MAX_HORIZON_AGE);
    }

    return new AgeYears(currentAge + earliest);
  }
}
